import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Zap, Plus, Play, Trash2, CheckCircle2, AlertCircle } from "lucide-react";
import { PRESET_ICONS, TRIGGER_MAP } from "./automations/constants";
import { TestRunModal } from "./automations/TestRunModal";
import { useAppBase } from "@/hooks/useAppBase";
import { fmtDateTime } from "@/lib/format";

/**
 * Automations page — list + presets library.
 * Create/edit lives on its own route `${base}/automations/:id`.
 */
export default function Automations() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const base = useAppBase();
  const isAdmin = base === "/admin";
  const [items, setItems] = useState([]);
  const [presets, setPresets] = useState([]);
  const [testing, setTesting] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);   // {id, name}

  const load = async () => {
    const calls = [api.get("/automations")];
    if (!isAdmin) calls.push(api.get("/automations/presets"));
    const [a, p] = await Promise.all(calls);
    setItems(a.data);
    setPresets(p?.data || []);
  };
  useEffect(() => {
    load();
    const tm = setInterval(() => { if (!testing) load(); }, 30000);
    return () => clearInterval(tm);
  }, [testing]); // eslint-disable-line

  const installed = useMemo(() => new Set(items.map((i) => i.preset_key).filter(Boolean)), [items]);

  const installPreset = async (key) => {
    try {
      const r = await api.post(`/automations/presets/${key}/install`);
      toast.success(r.data.already_installed ? t("automations.alreadyInstalled") : t("automations.installedToast", { name: r.data.name }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.error")); }
  };

  const toggle = async (a) => {
    try {
      await api.patch(`/automations/${a.id}`, { ...a, enabled: !a.enabled });
      toast.success(a.enabled ? t("automations.deactivated") : t("automations.activated"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.error")); }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/automations/${confirmDel.id}`);
      toast.success(t("automations.deleted"));
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  return (
    <div data-testid="automations-page" className="space-y-8">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-4xl font-black tracking-tight inline-flex items-center gap-3">
            <Zap className="h-8 w-8 text-[color:var(--sp-brand)]" /> {t("automations.title")}
          </h1>
          <p className="text-[color:var(--sp-text-secondary)] mt-2 max-w-2xl">
            {t("automations.subtitle")}
          </p>
        </div>
        <button
          data-testid="automations-new-btn"
          onClick={() => nav(`${base}/automations/new`)}
          className="sp-btn-primary inline-flex items-center gap-2"
        >
          <Plus className="h-4 w-4" /> {t("automations.new")}
        </button>
      </header>

      <section className="sp-card p-6" data-testid="presets-library" hidden={isAdmin} style={isAdmin ? { display: "none" } : undefined}>
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <h2 className="font-display text-xl font-black">{t("automations.presetsTitle")}</h2>
            <p className="text-xs text-[color:var(--sp-text-muted)] mt-1">
              {t("automations.presetsHint")}
            </p>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {presets.map((p) => {
            const Icon = PRESET_ICONS[p.icon] || Zap;
            const isInstalled = installed.has(p.key);
            return (
              <div key={p.key} data-testid={`preset-${p.key}`} className="p-4 rounded-2xl border border-[color:var(--sp-border)] bg-white hover:border-[color:var(--sp-brand)]/40 transition-colors">
                <div className="flex items-start gap-3">
                  <span className={`h-9 w-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isInstalled ? "bg-emerald-50 text-emerald-600" : "bg-[color:var(--sp-brand)]/10 text-[color:var(--sp-brand)]"}`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">{p.label}</div>
                    <div className="text-xs text-[color:var(--sp-text-muted)] mt-1 line-clamp-2">{p.description}</div>
                  </div>
                </div>
                <div className="mt-3">
                  {isInstalled ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full">
                      <CheckCircle2 className="h-3 w-3" /> {t("automations.installed")}
                    </span>
                  ) : (
                    <button data-testid={`install-${p.key}`} onClick={() => installPreset(p.key)} className="text-xs font-bold text-[color:var(--sp-brand)] hover:underline">
                      + {t("automations.install")}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="sp-card overflow-hidden">
        <div className="p-6 border-b border-[color:var(--sp-border)]">
          <h2 className="font-display text-xl font-black">{t("automations.activeTitle")}</h2>
          <p className="text-xs text-[color:var(--sp-text-muted)] mt-1">
            {items.length === 0
              ? t("automations.noneInstalled")
              : t("automations.summary", { count: items.length, active: items.filter((i) => i.enabled).length })}
          </p>
        </div>
        {items.length === 0 ? (
          <div className="p-12 text-center text-[color:var(--sp-text-muted)]">
            <Zap className="h-8 w-8 mx-auto mb-3 text-[color:var(--sp-border-strong)]" />
            {t("automations.emptyState")}
          </div>
        ) : (
          <div className="divide-y divide-[color:var(--sp-border)]">
            {items.map((a) => {
              const T = TRIGGER_MAP[a.trigger?.type] || TRIGGER_MAP.manual;
              const TIcon = T.icon;
              return (
                <div key={a.id} data-testid={`auto-row-${a.id}`} className="p-5 flex items-center gap-4 hover:bg-[color:var(--sp-subtle)]/60 transition-colors">
                  <button
                    data-testid={`toggle-${a.id}`}
                    onClick={() => toggle(a)}
                    className={`w-11 h-6 rounded-full flex-shrink-0 inline-flex items-center transition-colors ${a.enabled ? "bg-[color:var(--sp-brand)] justify-end" : "bg-slate-300 justify-start"} px-0.5`}
                    title={a.enabled ? t("automations.activate") : t("automations.deactivate")}
                    aria-pressed={a.enabled}
                  >
                    <span className="h-5 w-5 rounded-full bg-white shadow" />
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm truncate">{a.name}</span>
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                        <TIcon className="h-3 w-3" /> {t(T.label)}
                      </span>
                    </div>
                    <div className="text-[11px] text-[color:var(--sp-text-muted)] mt-1 flex items-center gap-3 flex-wrap">
                      <span>{t("automations.actions", { count: a.actions?.length || 0 })}</span>
                      <span>{t("automations.executions")} <strong className="text-[color:var(--sp-text)]">{a.run_count || 0}</strong></span>
                      {a.last_run_at && (
                        <span>{t("automations.lastRun", {
                          when: fmtDateTime(a.last_run_at, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }),
                        })}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button data-testid={`test-${a.id}`} onClick={() => setTesting(a)} title={t("automations.testTooltip")} className="text-xs font-bold px-3 py-1.5 rounded-full hover:bg-[color:var(--sp-subtle)] inline-flex items-center gap-1">
                      <Play className="h-3 w-3" /> {t("automations.test")}
                    </button>
                    <button data-testid={`edit-${a.id}`} onClick={() => nav(`${base}/automations/${a.id}`)} className="text-xs font-bold px-3 py-1.5 rounded-full hover:bg-[color:var(--sp-subtle)]">{t("common.edit")}</button>
                    <button data-testid={`delete-${a.id}`} onClick={() => setConfirmDel({ id: a.id, name: a.name })} className="p-1.5 text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-danger)]"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {testing && (
        <TestRunModal automation={testing} onClose={() => setTesting(null)} onRan={load} />
      )}

      {confirmDel && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
             onClick={() => setConfirmDel(null)} data-testid="automations-confirm-dialog">
          <div className="sp-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <span className="h-10 w-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="h-5 w-5" />
              </span>
              <div>
                <h3 className="font-display text-lg font-black">{t("automations.deleteTitle")}</h3>
                <p className="text-sm text-[color:var(--sp-text-secondary)] mt-1">
                  {t("automations.deleteBody", { name: confirmDel.name })}
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)}
                className="px-4 py-2 rounded-xl border border-[color:var(--sp-border)] text-sm font-bold hover:bg-[color:var(--sp-subtle)]">
                {t("common.cancel")}
              </button>
              <button onClick={doDelete}
                data-testid="automations-confirm-delete"
                className="px-4 py-2 rounded-xl bg-[color:var(--sp-danger)] text-white text-sm font-bold hover:opacity-90">
                {t("common.delete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
